import { useMemo } from 'react';

import {
  Alert,
  Button,
  Container,
  Grid,
  PasswordInput,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle } from '@tabler/icons-react';
import { handleSubmitError } from '@utils/forms';

import { useTranslation } from 'react-i18next';

import useQueryParams from '@hooks/queryParams/useQueryParams';
import useAuth from '@hooks/useAuth';
import useGRecaptcha from '@hooks/useGRecaptcha';

export default function LoginForm() {
  // ==========================================================================
  // General
  // ==========================================================================
  const { getRecaptchaToken } = useGRecaptcha();
  const { signin, isLoading } = useAuth();
  const { t } = useTranslation();

  const [params, setParams] = useQueryParams();

  const alert = useMemo(() => {
    const a = params.get('alert');
    return a && a.trim() !== '' ? a : null;
  }, [params]);

  // ==========================================================================
  // Form
  // ==========================================================================
  const initialValues = {
    email: '',
    password: '',
  };

  const form = useForm({
    initialValues,
  });

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const onSubmit = async (values: typeof initialValues) => {
    try {
      const recaptchaToken = await getRecaptchaToken('SignIn');

      await signin({ body: values, recaptchaToken });
    } catch (e) {
      handleSubmitError(e, form);
    }
  };

  // ==========================================================================
  // Render
  // ==========================================================================
  return (
    <Container w="100%" size="40rem">
      <Text ta="center" fw="bold" size="1.5rem">
        {t('auth.login.title')}
      </Text>

      <Text ta="center" mt="md">
        {t('auth.login.noAccount')}{' '}
        <Text
          span
          c="brand"
          style={{ cursor: 'pointer' }}
          onClick={() => {
            params.set('p', 'register');
            setParams(params);
          }}
        >
          {t('auth.login.registerLink')}
        </Text>
      </Text>

      {alert && (
        <Alert mt="md" icon={<IconAlertCircle size={16} />}>
          {alert}
        </Alert>
      )}

      <form
        onSubmit={form.onSubmit((values) => {
          onSubmit(values);
        })}
      >
        <Grid mt="xl">
          <Grid.Col span={{ md: 6 }}>
            <TextInput
              label={t('auth.login.email.label')}
              placeholder={t('auth.login.email.placeholder')}
              required
              {...form.getInputProps('email')}
            />
          </Grid.Col>
          <Grid.Col span={{ md: 6 }}>
            <PasswordInput
              label={t('auth.login.password.label')}
              placeholder={t('auth.login.password.placeholder')}
              required
              {...form.getInputProps('password')}
            />
          </Grid.Col>
        </Grid>

        <Text
          ta="center"
          mt="md"
          c="brand"
          style={{ cursor: 'pointer' }}
          onClick={() => {
            params.set('p', 'password-reset-request');
            setParams(params);
          }}
        >
          {t('auth.login.forgotPassword')}
        </Text>

        {form.errors.general && (
          <Text c="red" size="sm" mt="xl" ta="center">
            {form.errors.general}
          </Text>
        )}
        <Button
          type="submit"
          mx="auto"
          mt="xl"
          style={{ display: 'block' }}
          loading={isLoading}
        >
          {t('auth.login.submit')}
        </Button>
      </form>
    </Container>
  );
}
